import { useState, useCallback, useMemo, useEffect } from 'react'
import { useApiFetch, useApiMutation } from '../../hooks/useApiFetch.js'
import { useUIStore } from '../../stores/uiStore.js'
import { Button, Select, Input, Textarea, Modal, Badge, Spinner, Tabs, type TabItem } from '../../components/ui/index.js'
import { cn } from '../../lib/cn.js'
import styles from './AdminPromptBlocks.module.css'

// ── Types ──

interface AppSetting {
  key: string
  value: unknown
  category: string
  label: string
  description: string | null
}

interface PromptBlock {
  id: string
  blockType: string
  slug: string
  nameKo: string
  nameEn: string
  promptText: string
  isBuiltin: boolean
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

interface BlocksResponse {
  blocks: PromptBlock[]
}

// ── Constants ──

const BLOCK_TYPES = ['STYLE', 'POSE', 'WARDROBE', 'CAMERA'] as const

const TYPE_LABELS: Record<string, string> = {
  STYLE: '스타일',
  POSE: '포즈',
  WARDROBE: '의상',
  CAMERA: '카메라',
}

const TYPE_TABS: TabItem[] = [
  { key: 'ALL', label: '전체' },
  { key: 'STYLE', label: '스타일' },
  { key: 'POSE', label: '포즈' },
  { key: 'WARDROBE', label: '의상' },
  { key: 'CAMERA', label: '카메라' },
]

// ── Component ──

export function AdminPromptBlocks() {
  const { addToast } = useUIStore()

  const [activeTab, setActiveTab] = useState('ALL')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editBlock, setEditBlock] = useState<PromptBlock | null>(null)

  // 폼 상태
  const [formType, setFormType] = useState<string>('STYLE')
  const [formSlug, setFormSlug] = useState('')
  const [formNameKo, setFormNameKo] = useState('')
  const [formNameEn, setFormNameEn] = useState('')
  const [formPromptText, setFormPromptText] = useState('')
  const [formSortOrder, setFormSortOrder] = useState(0)

  // 데이터 로드
  const { data, refetch, isLoading } = useApiFetch<BlocksResponse>(
    '/api/admin/prompt-blocks?includeInactive=true'
  )

  // 프롬프트 설정 (BASE, NEGATIVE, QUALITY)
  const { data: settingsData, refetch: refetchSettings } = useApiFetch<AppSetting[]>(
    '/api/admin/settings?category=ai_prompt'
  )

  const { trigger: patchSetting } = useApiMutation<unknown, Record<string, unknown>>(
    '/api/admin/settings',
    'PATCH',
  )

  const [editSetting, setEditSetting] = useState<AppSetting | null>(null)
  const [settingValue, setSettingValue] = useState('')

  const promptSettings = useMemo(() => {
    if (!settingsData) return []
    // settingsData can be { success, data } or just data array depending on API
    const list = Array.isArray(settingsData) ? settingsData : (settingsData as any)?.data ?? []
    return list as AppSetting[]
  }, [settingsData])

  const openSettingEdit = useCallback((setting: AppSetting) => {
    setEditSetting(setting)
    setSettingValue(typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value))
  }, [])

  const handleSaveSetting = useCallback(async () => {
    if (!editSetting) return
    const res = await patchSetting({
      key: editSetting.key,
      value: settingValue,
      category: 'ai_prompt',
      label: editSetting.label,
      description: editSetting.description ?? '',
    })
    if (res) {
      addToast({ type: 'success', message: '설정이 저장되었습니다.' })
      setEditSetting(null)
      refetchSettings()
    }
  }, [editSetting, settingValue, patchSetting, addToast, refetchSettings])

  const { trigger: createBlock, isLoading: creating } = useApiMutation<unknown, Record<string, unknown>>(
    '/api/admin/prompt-blocks',
    'POST',
  )

  const { trigger: updateBlock, isLoading: updating } = useApiMutation<unknown, Record<string, unknown>>(
    '/api/admin/prompt-blocks',
    'PUT',
  )

  const { trigger: deleteBlock } = useApiMutation<unknown, never>(
    '/api/admin/prompt-blocks',
    'DELETE',
  )

  const { trigger: seedBlocks, isLoading: seeding } = useApiMutation<unknown, never>(
    '/api/admin/prompt-blocks/seed',
    'POST',
  )

  // 필터링된 블록
  const filteredBlocks = useMemo(() => {
    if (!data?.blocks) return []
    if (activeTab === 'ALL') return data.blocks
    return data.blocks.filter((b) => b.blockType === activeTab)
  }, [data, activeTab])

  // 타입별 그룹핑
  const groupedBlocks = useMemo(() => {
    const groups: Record<string, PromptBlock[]> = {}
    for (const type of BLOCK_TYPES) {
      groups[type] = filteredBlocks.filter((b) => b.blockType === type)
    }
    return groups
  }, [filteredBlocks])

  const displayTypes = activeTab === 'ALL' ? BLOCK_TYPES : [activeTab]

  // 생성 폼 초기화
  const resetForm = useCallback(() => {
    setFormType('STYLE')
    setFormSlug('')
    setFormNameKo('')
    setFormNameEn('')
    setFormPromptText('')
    setFormSortOrder(0)
  }, [])

  const openCreateModal = useCallback(() => {
    resetForm()
    setShowCreateModal(true)
  }, [resetForm])

  const openEditModal = useCallback((block: PromptBlock) => {
    setEditBlock(block)
    setFormNameKo(block.nameKo)
    setFormNameEn(block.nameEn)
    setFormPromptText(block.promptText)
    setFormSortOrder(block.sortOrder)
  }, [])

  // 생성
  const handleCreate = useCallback(async () => {
    if (!formSlug || !formNameKo || !formNameEn || !formPromptText) {
      addToast({ type: 'error', message: '모든 필드를 입력해주세요.' })
      return
    }

    const res = await createBlock({
      blockType: formType,
      slug: formSlug.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
      nameKo: formNameKo,
      nameEn: formNameEn,
      promptText: formPromptText,
      sortOrder: formSortOrder,
    })

    if (res) {
      addToast({ type: 'success', message: '블록이 생성되었습니다.' })
      setShowCreateModal(false)
      refetch()
    } else {
      addToast({ type: 'error', message: '블록 생성 실패' })
    }
  }, [formType, formSlug, formNameKo, formNameEn, formPromptText, formSortOrder, createBlock, addToast, refetch])

  // 수정
  const handleUpdate = useCallback(async () => {
    if (!editBlock) return

    const res = await updateBlock(
      { nameKo: formNameKo, nameEn: formNameEn, promptText: formPromptText, sortOrder: formSortOrder },
      { pathOverride: `/api/admin/prompt-blocks/${editBlock.id}` },
    )

    if (res) {
      addToast({ type: 'success', message: '블록이 수정되었습니다.' })
      setEditBlock(null)
      refetch()
    } else {
      addToast({ type: 'error', message: '블록 수정 실패' })
    }
  }, [editBlock, formNameKo, formNameEn, formPromptText, formSortOrder, updateBlock, addToast, refetch])

  // 활성/비활성 토글
  const handleToggleActive = useCallback(async (block: PromptBlock) => {
    const res = await updateBlock(
      { isActive: !block.isActive },
      { pathOverride: `/api/admin/prompt-blocks/${block.id}` },
    )

    if (res) {
      addToast({ type: 'success', message: block.isActive ? '블록이 비활성화되었습니다.' : '블록이 활성화되었습니다.' })
      refetch()
    }
  }, [updateBlock, addToast, refetch])

  // 삭제
  const handleDelete = useCallback(async (block: PromptBlock) => {
    if (block.isBuiltin) {
      addToast({ type: 'error', message: 'Builtin 블록은 삭제할 수 없습니다. 비활성화를 사용해주세요.' })
      return
    }

    if (!confirm(`"${block.nameKo}" 블록을 삭제하시겠습니까?`)) return

    const res = await deleteBlock(undefined, { pathOverride: `/api/admin/prompt-blocks/${block.id}` })
    if (res !== undefined) {
      addToast({ type: 'success', message: '블록이 삭제되었습니다.' })
      refetch()
    }
  }, [deleteBlock, addToast, refetch])

  // 시드 실행
  const handleSeed = useCallback(async () => {
    if (!confirm('시드 데이터를 실행하시겠습니까? 기존 builtin 블록의 promptText가 업데이트됩니다.')) return

    const res = await seedBlocks(undefined)
    if (res) {
      addToast({ type: 'success', message: '시드 데이터 적용 완료' })
      refetch()
    }
  }, [seedBlocks, addToast, refetch])

  if (isLoading) {
    return <div className={styles.root}><Spinner /></div>
  }

  return (
    <div className={styles.root}>
      {/* 헤더 */}
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>프롬프트 블록 관리</h2>
        <div className={styles.headerActions}>
          <Button size="sm" variant="secondary" onClick={handleSeed} disabled={seeding}>
            {seeding ? '시딩 중...' : '시드 데이터'}
          </Button>
          <Button size="sm" variant="primary" onClick={openCreateModal}>
            블록 추가
          </Button>
        </div>
      </div>

      {/* 기본 프롬프트 설정 */}
      {promptSettings.length > 0 && (
        <section className={styles.typeSection}>
          <div className={styles.typeSectionHeader}>
            <h3 className={styles.typeSectionTitle}>기본 프롬프트 설정</h3>
            <span className={styles.blockCount}>BASE / NEGATIVE / QUALITY</span>
          </div>
          <div className={styles.blockGrid}>
            {promptSettings.map((s) => (
              <div key={s.key} className={styles.blockCard}>
                <div className={styles.blockCardHeader}>
                  <div className={styles.blockMeta}>
                    <h4 className={styles.blockName}>{s.label}</h4>
                    <span className={styles.blockSlug}>{s.key}</span>
                  </div>
                </div>
                <p className={styles.blockPrompt}>
                  {typeof s.value === 'string' ? s.value : JSON.stringify(s.value)}
                </p>
                <div className={styles.blockActions}>
                  <Button size="sm" variant="secondary" onClick={() => openSettingEdit(s)}>
                    수정
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 탭 필터 */}
      <Tabs items={TYPE_TABS} value={activeTab} onChange={setActiveTab} />

      {/* 블록 목록 — 타입별 섹션 */}
      {displayTypes.map((type) => {
        const blocks = groupedBlocks[type] ?? []
        return (
          <section key={type} className={styles.typeSection}>
            <div className={styles.typeSectionHeader}>
              <h3 className={styles.typeSectionTitle}>{TYPE_LABELS[type]} ({type})</h3>
              <span className={styles.blockCount}>{blocks.length}개</span>
            </div>

            {blocks.length === 0 ? (
              <p className={styles.emptyText}>등록된 블록이 없습니다.</p>
            ) : (
              <div className={styles.blockGrid}>
                {blocks.map((block) => (
                  <div
                    key={block.id}
                    className={cn(styles.blockCard, !block.isActive && styles.blockCardInactive)}
                  >
                    <div className={styles.blockCardHeader}>
                      <div className={styles.blockMeta}>
                        <h4 className={styles.blockName}>{block.nameKo}</h4>
                        <span className={styles.blockSlug}>{block.slug}</span>
                      </div>
                      <div className={styles.blockBadges}>
                        {block.isBuiltin && <Badge color="blue">Builtin</Badge>}
                        <Badge color={block.isActive ? 'green' : 'gray'}>
                          {block.isActive ? '활성' : '비활성'}
                        </Badge>
                      </div>
                    </div>

                    <p className={styles.blockPrompt}>{block.promptText}</p>

                    <div className={styles.blockActions}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(block)}
                      >
                        {block.isActive ? '비활성화' : '활성화'}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => openEditModal(block)}>
                        수정
                      </Button>
                      {!block.isBuiltin && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(block)}>
                          삭제
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )
      })}

      {/* 생성 모달 */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="프롬프트 블록 추가"
      >
        <div className={styles.modalContent}>
          <div className={styles.modalRow}>
            <Select
              label="블록 타입"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              options={BLOCK_TYPES.map((t) => ({ value: t, label: `${TYPE_LABELS[t]} (${t})` }))}
            />
            <Input
              label="Slug"
              value={formSlug}
              onChange={(e) => setFormSlug(e.target.value)}
              placeholder="예: SUNSET_BEACH"
            />
          </div>
          <div className={styles.modalRow}>
            <Input
              label="이름 (한글)"
              value={formNameKo}
              onChange={(e) => setFormNameKo(e.target.value)}
              placeholder="예: 선셋 비치"
            />
            <Input
              label="이름 (영문)"
              value={formNameEn}
              onChange={(e) => setFormNameEn(e.target.value)}
              placeholder="예: Sunset Beach"
            />
          </div>
          <Textarea
            label="프롬프트 텍스트"
            value={formPromptText}
            onChange={(e) => setFormPromptText(e.target.value)}
            placeholder="프롬프트 텍스트를 입력하세요"
            rows={5}
          />
          <Input
            label="정렬 순서"
            type="number"
            value={String(formSortOrder)}
            onChange={(e) => setFormSortOrder(Number(e.target.value))}
          />
          <div className={styles.modalActions}>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>취소</Button>
            <Button variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? '생성 중...' : '생성'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* 설정 수정 모달 */}
      <Modal
        open={!!editSetting}
        onClose={() => setEditSetting(null)}
        title={`설정 수정: ${editSetting?.label ?? ''}`}
      >
        {editSetting && (
          <div className={styles.modalContent}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', margin: 0 }}>
              {editSetting.description}
            </p>
            <Textarea
              label="값"
              value={settingValue}
              onChange={(e) => setSettingValue(e.target.value)}
              rows={8}
            />
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setEditSetting(null)}>취소</Button>
              <Button variant="primary" onClick={handleSaveSetting}>저장</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 수정 모달 */}
      <Modal
        open={!!editBlock}
        onClose={() => setEditBlock(null)}
        title={`블록 수정: ${editBlock?.nameKo ?? ''}`}
      >
        {editBlock && (
          <div className={styles.modalContent}>
            <div className={styles.modalRow}>
              <Input label="블록 타입" value={editBlock.blockType} disabled />
              <Input label="Slug" value={editBlock.slug} disabled />
            </div>
            <div className={styles.modalRow}>
              <Input
                label="이름 (한글)"
                value={formNameKo}
                onChange={(e) => setFormNameKo(e.target.value)}
              />
              <Input
                label="이름 (영문)"
                value={formNameEn}
                onChange={(e) => setFormNameEn(e.target.value)}
              />
            </div>
            <Textarea
              label="프롬프트 텍스트"
              value={formPromptText}
              onChange={(e) => setFormPromptText(e.target.value)}
              rows={5}
            />
            <Input
              label="정렬 순서"
              type="number"
              value={String(formSortOrder)}
              onChange={(e) => setFormSortOrder(Number(e.target.value))}
            />
            <div className={styles.modalActions}>
              <Button variant="ghost" onClick={() => setEditBlock(null)}>취소</Button>
              <Button variant="primary" onClick={handleUpdate} disabled={updating}>
                {updating ? '수정 중...' : '수정'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
